import Phaser from 'phaser';
import conversationData from '../scripts/Conversations.js';
import * as fontConfig from '../scripts/FontConfig.js';

export default class FriendScene extends Phaser.Scene {
  constructor() {
    super('FriendScene');
  }

  init(data) {
    // Extract friend data passed from BuildingScene
    this.friendData = data.friendData || null;
    this.doorData = data.doorData || null;
    
    console.log('FriendScene init - Friend data:', this.friendData);
    console.log('FriendScene init - Door data:', this.doorData);
    
    // Extract friend name from object name (e.g., "friend-mo" -> "mo")
    this.friendName = this.friendData?.name?.split('-')[1] || 'mo';
    console.log('Friend name extracted:', this.friendName);
    
    // Find the conversation data for this friend
    this.conversation = conversationData.conversations.find(conv => 
      conv.name.toLowerCase() === this.friendName.toLowerCase()
    );
    
    if (!this.conversation) {
      console.error('No conversation found for friend:', this.friendName);
      // Fallback to first conversation
      this.conversation = conversationData.conversations[0];
    }
    
    console.log('Using conversation:', this.conversation);
    
    // Initialize conversation state
    this.currentQuestionId = 1;
    this.conversationStep = 0; // 0-3 for different mood states
    
    // Initialize data recording object
    this.gameData = {
      friend: this.friendName,
      startTime: Date.now(),
      answers: [],
      reflections: [] // Will be filled in ReflectionScene
    };
    
    // Track timing for current question
    this.questionStartTime = null;
    
    // Store placeholder positions for UI elements
    this.placeholders = {};
    
    // Keyboard navigation state
    this.selectedOptionIndex = -1; // -1 means no selection
    this.keyboardNavigationEnabled = false;
  }

  create() {
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;
    this.add.image(width / 2, height / 2, 'backgroundScreen').setOrigin(0.5);

    // Load and setup the conversation tilemap
    this.setupTilemap();
    
    // Create UI elements based on tilemap placeholders
    this.createTilemapBasedUI();
    
    // Setup keyboard navigation
    this.setupKeyboardNavigation();
    
    // Add fade-in transition
    this.cameras.main.fadeIn(300, 0, 0, 0);
    
    // Start the conversation
    this.showCurrentQuestion();
  }

  setupTilemap() {
    // Create the tilemap from the JSON
    this.map = this.make.tilemap({ key: 'conversation' });
    
    // Add the tileset to the tilemap
    const uiTileset = this.map.addTilesetImage('ui', 'ui');
    const speechbubbles = this.map.addTilesetImage('speechbubbles', 'speechbubbles');
    
    // Render background layers
    const backgroundBg = this.map.createLayer('Background-bg', uiTileset);
    const background = this.map.createLayer('Background', uiTileset);
    const backgroundFg = this.map.createLayer('Background-fg', uiTileset);
    backgroundFg.setDepth(24);
    const base = this.map.createLayer('Base', [uiTileset, speechbubbles]);
    base.setDepth(25);
    
    // Extract placeholder positions from object layers
    this.extractPlaceholders();
  }
  
  extractPlaceholders() {
    // Get the object layer containing UI placeholders
    const objectLayer = this.map.getObjectLayer('placeholders');
    
    if (objectLayer) {
      objectLayer.objects.forEach(obj => {
        // Store placeholder positions (scaled by 2 to match layer scaling)
        this.placeholders[obj.name] = {
          x: obj.x,
          y: obj.y,
          width: obj.width,
          height: obj.height
        };
      });
    }
    
    console.log('Extracted placeholders:', this.placeholders);
  }
  
  createTilemapBasedUI() {
    // Create UI elements positioned using tilemap placeholders
    this.createRoomView();
    this.createMessageBoxes();
    this.createPortraits();
    this.createAnswerButtons();
    this.createExitButton();
  }
  
  createMessageBoxes() {
    // Friend message area
    if (this.placeholders.messageFriend) {
      const pos = this.placeholders.messageFriend;
      this.friendMessageBox = this.add.rectangle(
        pos.x + pos.width/2, 
        pos.y + pos.height/2, 
        pos.width, 
        pos.height
      );
      this.friendMessageBox.setDepth(30); // Above base layer
      
      this.friendMessageText = fontConfig.createText(
        this, 
        pos.x + 10, // Left-aligned with padding
        pos.y + 10, // Top-aligned with padding
        "", 
        "default", {
          align: 'left',
          wordWrap: { width: pos.width - 20 }
        }
      );
      this.friendMessageText.setOrigin(0, 0); // Top-left origin
      this.friendMessageText.setDepth(31); // Above message box
    }
    
    // Hero message area (question display)
    if (this.placeholders.messageHero) {
      const pos = this.placeholders.messageHero;
      this.heroMessageBox = this.add.rectangle(
        pos.x + pos.width/2, 
        pos.y + pos.height/2, 
        pos.width, 
        pos.height
      );
      this.heroMessageBox.setDepth(30); // Above base layer
      
      this.heroMessageText = fontConfig.createText(
        this, 
        pos.x + 10, // Left-aligned with padding
        pos.y + 10, // Top-aligned with padding
        "", 
        "default", {
          align: 'left',
          wordWrap: { width: pos.width - 20 }
        }
      );
      this.heroMessageText.setOrigin(0, 0); // Top-left origin
      this.heroMessageText.setDepth(31); // Above message box
    }
  }

  createPortraits() {
    // Friend portrait
    if (this.placeholders.friend) {
      const pos = this.placeholders.friend;
      
      // Start with mood for initial question (question ID 1)
      const initialMood = this.getFriendMoodForQuestionId(1);
      this.friendPortrait = this.add.image(
        pos.x + pos.width/2, 
        pos.y + pos.height/2, 
        `${this.friendName}-${initialMood}`
      );
      
      // Scale from 320x320 to fit in 160x160 placeholder
      this.friendPortrait.setScale(pos.width / 320);
      this.friendPortrait.setDepth(32); // Above message boxes
    }
    
    // Hero portrait - start with mood for initial question (question ID 1)
    if (this.placeholders.hero) {
      const pos = this.placeholders.hero;
      const initialHeroMood = this.getHeroMoodForQuestionId(1);
      this.heroPortrait = this.add.image(
        pos.x + pos.width/2, 
        pos.y + pos.height/2, 
        `hero-boy-${initialHeroMood}`
      );
      
      // Scale from 320x320 to fit in 160x160 placeholder
      this.heroPortrait.setScale(pos.width / 320);
      this.heroPortrait.setDepth(32); // Above message boxes
    }
    
    // Friend name display
    if (this.placeholders.friendName) {
      const pos = this.placeholders.friendName;
      this.friendNameText = fontConfig.createText(
        this, 
        pos.x + 5, // Left-aligned with small padding
        pos.y + 5, // Top-aligned with small padding
        this.friendName, 
        "small",
        {
          align: "left",
          fill: "#cccccc"
        }
      );
      this.friendNameText.setOrigin(0, 0); // Top-left origin
      this.friendNameText.setDepth(33); // Above portraits
    }
  }

  createRoomView() {
    // Room view with background image and character sprite
    if (this.placeholders.roomView) {
      const pos = this.placeholders.roomView;
      
      // Create room background image (scaled 2x)
      this.roomBackground = this.add.image(
        pos.x + pos.width/2, 
        pos.y + pos.height/2, 
        `room-${this.friendName}`
      );
      
      // Scale the room background to 2x the placeholder size
      this.roomBackground.setDisplaySize(pos.width * 2, pos.height * 2);
      this.roomBackground.setDepth(10); // Far behind all UI elements
      
      // Create character sprite in the middle of the room (scaled 2x)
      const initialRoomMood = this.getRoomMoodForQuestionId(1);
      this.roomCharacter = this.add.image(
        pos.x + pos.width/2, 
        pos.y + pos.height/2, 
        `${this.friendName}-room-${initialRoomMood}`
      );
      this.addHypnoEffect(pos.x + pos.width/2, pos.y + pos.height/2);
      
      // Character sprite scaled 2x from original calculation
      const characterScale = Math.min(pos.width / 200, pos.height / 200) * 2; // Scale 2x larger
      this.roomCharacter.setScale(characterScale);
      this.roomCharacter.setDepth(20); // Above room background but behind all UI
    }
  }
  addHypnoEffect(posX, posY) {
    // Placeholder for adding hypnotic visual effects if needed
    this.hypnoEffect = this.add.image(posX, posY, 'hypno');
    this.tweens.add({
      targets: this.hypnoEffect,
      ease: 'Linear',
      rotation: 20,
      scale: [1.6, 2.4, 1.6], // Scaled 2x from original [0.8, 1.2, 0.8]
      repeat: -1,
      duration: 10000
    });
    this.hypnoEffect.setScale(4); // Scaled 2x from original scale of 2
    this.hypnoEffect.setDepth(15); // Behind character but above room background
  }
  hideHypnoEffect() {
    if (this.hypnoEffect) {
      this.hypnoEffect.setVisible(false);
    }
  }
  showHypnoEffect() {
    if (this.hypnoEffect) {
      this.hypnoEffect.setVisible(true);
    }
  }
  
  createAnswerButtons() {
    // Create answer buttons based on option placeholders
    this.answerButtons = [];
    this.optionSelectedSprites = [];
    
    for (let i = 1; i <= 3; i++) {
      const optionKey = `option${i}`;
      const selectedKey = `option${i}-selected`;
      
      if (this.placeholders[optionKey]) {
        const pos = this.placeholders[optionKey];
        
        // Create button with custom styling
        const button = this.add.rectangle(
          pos.x + pos.width/2, 
          pos.y + pos.height/2, 
          pos.width, 
          pos.height,
          0xFFFFFF // Default background color: #6B9196
        );
        button.setStrokeStyle(1, 0xFFFFFF); // 1px stroke color: #ffffffff
        button.setInteractive({ useHandCursor: true });
        button.setDepth(34); // Above other UI elements
        
        // Create button text with left alignment
        const buttonText = fontConfig.createText(
          this, 
          pos.x + 15, // Left-aligned with padding
          pos.y + pos.height/2, 
          '', 
          "default", {
            align: 'left',
            wordWrap: { width: pos.width - 30 } // Account for left padding
          }
        );
        buttonText.setOrigin(0, 0.5); // Left-align horizontally, center vertically
        buttonText.setDepth(35); // Above buttons
        
        // Create selection indicator if placeholder exists
        let selectedSprite = null;
        if (this.placeholders[selectedKey]) {
          const selPos = this.placeholders[selectedKey];
          selectedSprite = this.add.rectangle(
            selPos.x + selPos.width/2, 
            selPos.y + selPos.height/2, 
            selPos.width, 
            selPos.height
          );
          selectedSprite.setVisible(false);
          selectedSprite.setDepth(36); // Above button text for selection feedback
        }
        
        // Store references
        button.buttonText = buttonText;
        button.optionIndex = i - 1; // Convert to 0-based index
        button.selectedSprite = selectedSprite;
        
        // Add interaction handlers
        button.on('pointerover', () => {
          // Clear keyboard selection when mouse hovers
          if (this.keyboardNavigationEnabled) {
            this.clearKeyboardSelection();
            this.selectedOptionIndex = button.optionIndex;
            this.applyKeyboardSelection();
          } else {
            button.setFillStyle(0x12E2FF); // Hover background color: #12E2FF
            if (selectedSprite) selectedSprite.setVisible(true);
          }
        });
        
        button.on('pointerout', () => {
          // Only clear hover state if not keyboard selected
          if (!this.keyboardNavigationEnabled || this.selectedOptionIndex !== button.optionIndex) {
            button.setFillStyle(0xFFFFFF); // Return to default background color: #6B9196
            if (selectedSprite) selectedSprite.setVisible(false);
          }
        });
        
        button.on('pointerdown', () => {
          this.selectAnswer(button.optionIndex);
        });
        
        this.answerButtons.push(button);
        this.optionSelectedSprites.push(selectedSprite);
      }
    }
  }

  createExitButton() {
    // Exit button - use fixed position in top-right if no placeholder
    const x = 750;
    const y = 50;
    
    this.exitButton = this.add.rectangle(x, y, 40, 40, 0xe74c3c);
    this.exitButton.setStrokeStyle(2, 0xc0392b);
    this.exitButton.setInteractive({ useHandCursor: true });
    this.exitButton.setDepth(37); // High depth for exit button

    const exitText = fontConfig.createText(this, x, y, 'X', "default");
    exitText.setOrigin(0.5);
    exitText.setDepth(38); // Above exit button
    
    // Add hover effects
    this.exitButton.on('pointerover', () => {
      this.exitButton.setFillStyle(0xc0392b);
    });
    
    this.exitButton.on('pointerout', () => {
      this.exitButton.setFillStyle(0xe74c3c);
    });
    
    this.exitButton.on('pointerdown', () => {
      this.exitToVillage();
    });
  }

  showCurrentQuestion() {
    // Record timing for this question
    this.questionStartTime = Date.now();
    
    // If this is the first question (id: 1), show the observation first
    if (this.currentQuestionId === 1) {
      const observationEntry = this.conversation.conversation.find(q => q.id === 0);
      if (observationEntry && observationEntry.observation && this.friendMessageText) {
        this.friendMessageText.setText(observationEntry.observation);
        fontConfig.animateText(this.friendMessageText, 20, 1000);
      }
    }
    
    // Find the current question in the conversation
    const currentQuestion = this.conversation.conversation.find(q => q.id === this.currentQuestionId);
    
    if (!currentQuestion) {
      console.error('Question not found:', this.currentQuestionId);
      this.completeConversation();
      return;
    }
    
    console.log('Showing question:', currentQuestion);
    
    // Show the question text in hero message area
    if (this.heroMessageText) {
      this.heroMessageText.setText(currentQuestion.question);
      fontConfig.animateText(this.heroMessageText, 20, 1000);
    }
    
    // Don't clear friend message area - let responses stay visible
    
    // Show the follow-up options
    const followUps = currentQuestion['follow-up'] || [];
    
    for (let i = 0; i < this.answerButtons.length; i++) {
      const button = this.answerButtons[i];
      
      if (i < followUps.length) {
        button.setVisible(true);
        button.buttonText.setVisible(true);
        button.buttonText.setText(followUps[i].text);
        fontConfig.animateText(button.buttonText, 20, 1500);

        button.followUpData = followUps[i];
      } else {
        button.setVisible(false);
        button.buttonText.setVisible(false);
      }
    }
    
    // Update mood/portraits based on conversation progress
    this.updateMood();
    
    // Enable keyboard navigation after options are shown
    this.enableKeyboardNavigation();
  }

  selectAnswer(optionIndex) {
    const button = this.answerButtons[optionIndex];
    const followUpData = button.followUpData;
    
    if (!followUpData) {
      console.error('No follow-up data for option:', optionIndex);
      return;
    }
    
    console.log('Selected answer:', followUpData);
    
    // Record this choice with timing
    const questionTiming = Date.now() - this.questionStartTime;
    this.gameData.answers.push({
      question: this.currentQuestionId,
      choice: optionIndex,
      timing: questionTiming
    });
    
    console.log('Recorded answer:', this.gameData.answers[this.gameData.answers.length - 1]);
    
    // Disable buttons temporarily to prevent double-clicking
    this.disableAnswerButtons();
    
    // Show the friend's response in the friend message area
    if (this.friendMessageText) {
      this.friendMessageText.setText(followUpData.response);
      fontConfig.animateText(this.friendMessageText, 50, 0);
    }
    
    // Check if conversation should continue
    if (followUpData.next === null) {
      // Conversation is complete - successful!
      this.time.delayedCall(800, () => {
        this.completeConversation();
      });
    } else {
      // Move to next question
      this.currentQuestionId = followUpData.next;
      this.conversationStep = Math.min(3, this.conversationStep + 1);
      
      // Much shorter delay before showing next question
      this.time.delayedCall(300, () => {
        this.showCurrentQuestion();
        this.enableAnswerButtons();
      });
    }
  }

  disableAnswerButtons() {
    this.answerButtons.forEach(button => {
      button.removeInteractive();
      button.setAlpha(0.6);
    });
    // Disable keyboard navigation as well
    this.disableKeyboardNavigation();
  }

  enableAnswerButtons() {
    this.answerButtons.forEach(button => {
      button.setInteractive({ useHandCursor: true });
      button.setAlpha(1.0);
    });
    // Re-enable keyboard navigation
    this.enableKeyboardNavigation();
  }

  getFriendMoodForQuestionId(questionId) {
    // Map question IDs to friend portrait moods as specified:

    const moodMap = {
      1: 'hypno',
      2: 'hypno', 
      3: 'angry',
      4: 'neutral',
      5: 'happy'
    };
    return moodMap[questionId] || 'happy'; // Default to happy for questions beyond 5
  }

  getHeroMoodForQuestionId(questionId) {
    // Map question IDs to hero portrait moods as specified:

    const moodMap = {
      1: 'angry',
      2: 'neutral', 
      3: 'neutral',
      4: 'happy',
      5: 'happy'
    };
    return moodMap[questionId] || 'happy'; // Default to happy for questions beyond 5
  }

  getRoomMoodForQuestionId(questionId) {
    // Map question IDs to room character moods as specified:

    const moodMap = {
      1: 'hypno',
      2: 'hypno', 
      3: 'neutral',
      4: 'neutral',
      5: 'happy'
    };
    return moodMap[questionId] || 'happy'; // Default to happy for questions beyond 5
  }

  updateMood() {
    // Update friend portrait based on current question ID
    if (this.friendPortrait) {
      const mood = this.getFriendMoodForQuestionId(this.currentQuestionId);
      this.friendPortrait.setTexture(`${this.friendName}-${mood}`);
    }
    
    // Show hypno effect only when character is in hypno mood (questions 1-2)
    const roomMood = this.getRoomMoodForQuestionId(this.currentQuestionId);
    if(roomMood === 'hypno'){
      this.showHypnoEffect();
    }else{
      this.hideHypnoEffect();
    }
    
    // Update hero portrait based on current question ID
    if (this.heroPortrait) {
      const mood = this.getHeroMoodForQuestionId(this.currentQuestionId);
      this.heroPortrait.setTexture(`hero-boy-${mood}`);
    }
    
    // Update room character sprite based on current question ID
    if (this.roomCharacter) {
      const mood = this.getRoomMoodForQuestionId(this.currentQuestionId);
      this.roomCharacter.setTexture(`${this.friendName}-room-${mood}`);
    }
  }

  completeConversation() {
    console.log('Conversation completed successfully!');
    
    // Calculate total conversation time
    const totalTime = Date.now() - this.gameData.startTime;
    this.gameData.totalTime = totalTime;
    
    console.log('Complete conversation data:', this.gameData);
    
    // Submit challenge data
    this.submitChallengeData();
    
    // Show completion message
    if (this.friendMessageText) {
      this.friendMessageText.setText(`Okay, ik ga mee!`);

    }
    
    // Check group to determine next scene
    this.time.delayedCall(3500, () => {
      // Get group from SessionManager
      const group = window.sessionManager ? window.sessionManager.group : (localStorage.getItem('hypnobreaker_group') || '2');
      
      if (parseInt(group) === 1) {
        console.log('Group 1: Skipping ReflectionScene, going directly to VillageScene');
        
        // Use Phaser's native camera fade out
        this.cameras.main.fadeOut(500, 0, 0, 0);
        
        // Listen for fade out complete event
        this.cameras.main.once(
          Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE,
          () => {
            // Go directly to VillageScene for group 1
            this.scene.start('VillageScene', { 
              fromFriend: true,
              friendName: this.friendName,
              success: true
            });
          }
        );
      } else {
        console.log('Group 2: Proceeding to ReflectionScene with data:', {
          friendName: this.friendName,
          success: true,
          gameData: this.gameData
        });
        
        // Use Phaser's native camera fade out
        this.cameras.main.fadeOut(500, 0, 0, 0);
        
        // Listen for fade out complete event
        this.cameras.main.once(
          Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE,
          () => {
            this.scene.start('ReflectionScene', {
              friendName: this.friendName,
              success: true,
              doorData: this.doorData,
              gameData: this.gameData  // Pass the complete data
            });
          }
        );
      }
    });
  }

  exitToVillage() {
    console.log('Player exited conversation early');
    
    // Use Phaser's native camera fade out
    this.cameras.main.fadeOut(500, 0, 0, 0);
    
    // Listen for fade out complete event
    this.cameras.main.once(
      Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE,
      () => {
        // Return to village scene without success
        this.scene.start('VillageScene');
      }
    );
  }

  async submitChallengeData() {
    if (window.sessionManager) {
      // Store the complete game data using SessionManager
      try {
        await window.sessionManager.storeGameData(this.gameData, 'friendScene');
        console.log('✅ FriendScene game data stored successfully');
      } catch (error) {
        console.error('❌ Error storing FriendScene game data:', error);
      }

      // Submit individual friend rescue as challenge data with friend-specific data
      const challengeData = {
        friendName: this.conversation.name,
        timeSpent: this.gameData.totalTime || 120,
        attemptsNeeded: this.conversationStep,
        dialogChoices: this.gameData.answers.map(answer => `option_${answer.choice}`),
        success: true,
        conversationData: this.gameData, // Include full conversation data
        friendIndex: this.getFriendIndex(this.friendName) // Add friend index for unique tracking
      };

      try {
        // Submit each friend rescue as a separate challenge event
        // Using challenge 4 but with unique friend identifier in the data
        await window.sessionManager.submitFriendChallenge(challengeData);
        console.log(`✅ Friend rescue challenge submitted for ${this.friendName}`);
        
        // Also submit with a unique challenge number per friend for separate tracking
        const friendSpecificChallengeNumber = 10 + this.getFriendIndex(this.friendName); // Challenge 10, 11, 12, 13, 14 for friends
        await window.sessionManager.submitChallenge(friendSpecificChallengeNumber, challengeData);
        console.log(`✅ Friend-specific challenge ${friendSpecificChallengeNumber} submitted for ${this.friendName}`);
      } catch (error) {
        console.error('❌ Error submitting challenge data:', error);
      }
    } else {
      console.warn('⚠️ SessionManager not available, cannot store game data');
    }
  }

  getFriendIndex(friendName) {
    // Map friend names to consistent indices
    const friendMap = {
      'mo': 0,
      'sam': 1,
      'julia': 2,
      'liam': 3,
      'louisa': 4
    };
    return friendMap[friendName.toLowerCase()] || 0;
  }

  setupKeyboardNavigation() {
    // Create keyboard cursors
    this.cursors = this.input.keyboard.createCursorKeys();
    
    // Add keyboard event listeners
    this.input.keyboard.on('keydown-UP', () => {
      this.navigateUp();
    });
    
    this.input.keyboard.on('keydown-DOWN', () => {
      this.navigateDown();
    });
    
    this.input.keyboard.on('keydown-ENTER', () => {
      this.confirmSelection();
    });
  }
  
  navigateUp() {
    if (!this.keyboardNavigationEnabled || !this.answerButtons) return;
    
    // Clear current selection visual state
    this.clearKeyboardSelection();
    
    // Move selection up
    if (this.selectedOptionIndex <= 0) {
      this.selectedOptionIndex = this.answerButtons.length - 1; // Wrap to last option
    } else {
      this.selectedOptionIndex--;
    }
    
    // Apply selection visual state
    this.applyKeyboardSelection();
  }
  
  navigateDown() {
    if (!this.keyboardNavigationEnabled || !this.answerButtons) return;
    
    // Clear current selection visual state
    this.clearKeyboardSelection();
    
    // Move selection down
    if (this.selectedOptionIndex >= this.answerButtons.length - 1) {
      this.selectedOptionIndex = 0; // Wrap to first option
    } else {
      this.selectedOptionIndex++;
    }
    
    // Apply selection visual state
    this.applyKeyboardSelection();
  }
  
  confirmSelection() {
    if (!this.keyboardNavigationEnabled || this.selectedOptionIndex < 0 || !this.answerButtons) return;
    
    // Select the currently highlighted option
    this.selectAnswer(this.selectedOptionIndex);
  }
  
  clearKeyboardSelection() {
    if (this.selectedOptionIndex >= 0 && this.answerButtons && this.answerButtons[this.selectedOptionIndex]) {
      const button = this.answerButtons[this.selectedOptionIndex];
      // Reset to default state
      button.setFillStyle(0xFFFFFF);
      if (button.selectedSprite) {
        button.selectedSprite.setVisible(false);
      }
    }
  }
  
  applyKeyboardSelection() {
    if (this.selectedOptionIndex >= 0 && this.answerButtons && this.answerButtons[this.selectedOptionIndex]) {
      const button = this.answerButtons[this.selectedOptionIndex];
      // Apply hover state
      button.setFillStyle(0x12E2FF);
      if (button.selectedSprite) {
        button.selectedSprite.setVisible(true);
      }
    }
  }
  
  enableKeyboardNavigation() {
    this.keyboardNavigationEnabled = true;
    // Start with first option selected if no selection exists
    if (this.selectedOptionIndex < 0 && this.answerButtons && this.answerButtons.length > 0) {
      this.selectedOptionIndex = 0;
      this.applyKeyboardSelection();
    }
  }
  
  disableKeyboardNavigation() {
    this.keyboardNavigationEnabled = false;
    this.clearKeyboardSelection();
    this.selectedOptionIndex = -1;
  }
}
